import './select.scss';
import Choices from 'choices.js';

/**
 * FormSelect component.
 *
 * Enhances a native <select> element with the Choices.js widget, accessibility
 * attributes, and custom templates. Supports single- and multi-select modes,
 * placeholder handling, and keyboard/blur auto-closing behaviors.
 */
class FormSelect {
    /**
     * Creates a new FormSelect instance.
     *
     * @param {HTMLSelectElement} $element - The native <select> element to enhance.
     * @param {Object} [options={}] - Optional configuration overrides.
     * @param {string} [options.initAttr="data-select"] - Data attribute used for initialization/root lookup.
     */
    constructor ($element, options) {
        /** @type {{initAttr: string}} */
        const defaults = {
            initAttr: 'data-select'
        };

        /**
         * Merged settings (defaults overridden by `options`).
         * @type {{initAttr: string}}
         */
        this.settings = Object.assign({}, defaults, options);

        /**
         * The underlying native <select> element.
         * @type {HTMLSelectElement}
         */
        this.$formSelect = $element;

        // Initialize only once per element.
        if (!this.$formSelect.classList.contains('initialized')) {
            this.initialize();
        }
    }

    /**
     * Initializes the Choices.js instance and sets up accessibility attributes
     * and supporting event handlers.
     *
     * DOM/ARIA behavior:
     * - Adds the "initialized" class to prevent duplicate setups.
     * - Reads configuration from data attributes:
     *   - data-select-multiple="true" to enable multi-select behavior.
     *   - data-select-placeholder="true" to enable a visual placeholder.
     *   - data-select-label to provide an aria-label when no <label> is present.
     * - Creates a Choices instance with custom item/choice templates which can
     *   prepend optional content via the option's `data-element-before` attribute.
     * - Wires up ARIA relationships between label and the Choices container.
     * - Adds auto-close behaviors for keyboard Tab and outside clicks in multi-select mode.
     *
     * @returns {void}
     */
    initialize () {
        this.$formSelect.classList.add('initialized');

        /** @type {string|null} */
        const choicesID = this.$formSelect.id;

        /**
         * Whether multiple selections are allowed.
         * Derived from `data-select-multiple="true"`.
         * @type {boolean}
         */
        const multipleChoices = this.$formSelect.dataset.selectMultiple === 'true';

        /**
         * Whether a placeholder should be rendered.
         * Derived from `data-select-placeholder="true"`.
         * @type {boolean}
         */
        const usePlaceholder = this.$formSelect.dataset.selectPlaceholder === 'true';

        /**
         * The Choices.js instance bound to this select.
         * @type {Choices}
         */
        this.formSelect = new Choices(this.$formSelect, {
            // General behavior
            closeDropdownOnSelect: true,
            allowHTML: true,
            removeItems: true,
            removeItemButton: multipleChoices,
            renderSelectedChoices: 'always',
            placeholder: usePlaceholder,
            placeholderValue: null,
            searchEnabled: false,
            searchChoices: false,
            itemSelectText: '',
            /**
             * For multi-selects, allow single-mode rendering of selected items.
             * @type {boolean}
             */
            singleModeForMultiSelect: multipleChoices,
            shouldSort: false,

            /**
             * Provides custom rendering templates for selected items and dropdown choices.
             * Allows injecting optional leading content via each option element's
             * `data-element-before` attribute.
             *
             * @param {(html:string)=>Element} strToEl - Converts an HTML string to an Element.
             * @param {(allowHTML:boolean, value:string)=>string} escapeForTemplate - Escapes text for safe templating (or passes through if allowHTML = true).
             * @returns {{item:function, choice:function}} - Template functions for Choices.js.
             */
            callbackOnCreateTemplates: function (strToEl, escapeForTemplate) {
                const itemSelectText = this.config.itemSelectText;
                const allowHTML = this.config.allowHTML;
                const removeItemButton = this.config.removeItemButton;

                return {
                    /**
                     * Template for a selected item (pill in the input/trigger).
                     * @param {{classNames: object}} param0
                     * @param {{id:number,label:string,value:string,active:boolean,disabled:boolean,selected:boolean,highlighted:boolean,element:HTMLElement}} data
                     * @returns {Element}
                     */
                    item: function ({ classNames }, data) {
                        const $element = data.element;
                        /** @type {string} */
                        let additionalElementBefore = '';

                        if ($element.dataset.elementBefore) {
                            additionalElementBefore = ' ' + $element.dataset.elementBefore;
                        }

                        const $button = removeItemButton ? strToEl('<button type="button" class="' + String(classNames.button) + '" data-button aria-label="Remove item: ' + String(data.value) + '">Remove item</button>') : '';
                        const $item = strToEl(
                            '<div' +
                            ' class="' + String(classNames.item) + ' ' +
                            String(data.highlighted ? classNames.highlightedState : classNames.itemSelectable) + ' ' +
                            String(data.selected ? classNames.selectedState : '') + '"' +
                            ' data-item' +
                            ' data-id="' + String(data.id) + '"' +
                            ' data-value="' + String(escapeForTemplate(allowHTML, data.value)) + '"' +
                            ' ' + String(data.disabled ? 'aria-disabled="true"' : '') +
                            '>' +
                            String(escapeForTemplate(allowHTML, additionalElementBefore)) +
                            String(escapeForTemplate(allowHTML, data.label)) +
                            '</div>'
                        );

                        if ($button) {
                            $item.appendChild($button);
                        }

                        return $item;
                    },

                    /**
                     * Template for a dropdown choice (option in the menu).
                     * @param {{classNames: object}} param0
                     * @param {{id:number,label:string,value:string,groupId:number,disabled:boolean,selected:boolean,element:HTMLElement}} data
                     * @returns {Element}
                     */
                    choice: function ({ classNames }, data) {
                        const $element = data.element;
                        /** @type {string} */
                        let additionalElementBefore = '';

                        if ($element.dataset.elementBefore) {
                            additionalElementBefore = ' ' + $element.dataset.elementBefore;
                        }
                        return strToEl(
                            '<div' +
                            ' class="' + String(classNames.item) + ' ' + String(classNames.itemChoice) + ' ' +
                            String(data.disabled ? classNames.itemDisabled : classNames.itemSelectable) + ' ' +
                            String(data.selected ? classNames.selectedState : '') + '"' +
                            ' data-select-text="' + String(itemSelectText) + '"' +
                            ' data-choice' +
                            ' ' + String(data.disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable') +
                            ' data-id="' + String(data.id) + '"' +
                            ' data-value="' + String(escapeForTemplate(allowHTML, data.value)) + '"' +
                            ' ' + String(data.groupId > 0 ? 'role="treeitem"' : 'role="option"') +
                            '>' +
                            String(escapeForTemplate(allowHTML, additionalElementBefore)) +
                            String(escapeForTemplate(allowHTML, data.label)) +
                            '</div>'
                        );
                    }
                };
            }
        });

        /**
         * Inner container element created by Choices (if present).
         * @type {HTMLElement|undefined}
         */
        const $choicesInner = this.$formSelect.closest('.choices__inner');

        /**
         * Root Choices container (wrapper).
         * @type {HTMLElement|undefined}
         */
        const $choices = $choicesInner?.parentElement;

        if ($choices) {
            // Correct ARIA roles for accessibility compliance (ARIA 1.2+).
            // Choices.js v11+ sometimes incorrectly uses role="listbox" on the wrapper.
            $choices.setAttribute('role', 'combobox');
            $choices.setAttribute('aria-haspopup', 'listbox');

            /**
             * Closest .form-field wrapper (if present) to wire up ARIA labeling.
             * @type {HTMLElement|null}
             */
            const $field = $choices.closest('.form-field');
            const $label = $field?.querySelector('label');

            if ($label) {
                // Prefer aria-labelledby if a label element is found.
                // We ensure it has a unique ID and remove any redundant aria-label.
                const labelFor = $label.getAttribute('for') || choicesID || Math.random().toString(36).substring(2, 11);
                const labelId = 'label-' + labelFor;
                $label.setAttribute('id', labelId);
                $choices.setAttribute('aria-labelledby', labelId);
                $choices.removeAttribute('aria-label');
            } else {
                // Fallback to aria-label from data attribute or native select.
                const fallbackLabel = this.$formSelect.dataset.selectLabel || this.$formSelect.getAttribute('aria-label') || this.$formSelect.getAttribute('title');
                if (fallbackLabel) {
                    $choices.setAttribute('aria-label', fallbackLabel);
                    $choices.removeAttribute('aria-labelledby');
                }
            }

            // Reflect required state from native select to the combobox container.
            if (this.$formSelect.required) {
                $choices.setAttribute('aria-required', 'true');
            }

            // Ensure the dropdown list has the listbox role to support role="option" children.
            const $choicesListDropdown = $choices.querySelector('.choices__list--dropdown');
            if ($choicesListDropdown) {
                $choicesListDropdown.setAttribute('role', 'listbox');

                // Provide an accessible name to the listbox (same as the combobox).
                const comboLabel = $choices.getAttribute('aria-label');
                const comboLabelledBy = $choices.getAttribute('aria-labelledby');

                if (comboLabelledBy) {
                    $choicesListDropdown.setAttribute('aria-labelledby', comboLabelledBy);
                } else if (comboLabel) {
                    $choicesListDropdown.setAttribute('aria-label', comboLabel);
                }

                if (choicesID) {
                    // Tie combobox to the dropdown listbox for accessibility.
                    const dropdownID = 'choices-dropdown-' + choicesID;
                    $choicesListDropdown.id = $choicesListDropdown.id || dropdownID;
                    $choices.setAttribute('aria-controls', $choicesListDropdown.id);
                }
            }

            // Avoid nested comboboxes (Choices.js might put it on an inner input)
            $choices.querySelectorAll('[role="combobox"]').forEach(($el) => {
                if ($el !== $choices) {
                    $el.removeAttribute('role');
                }
            });
        }

        /**
         * Closes the Choices dropdown if it is open.
         * Used to implement blur/keyboard-driven closing in multi-select mode.
         * @returns {void}
         */
        const autoCloseSelect = () => {
            if ($choices.classList.contains('is-open')) {
                // Simulate outside click/blur to close dropdown.
                $choices.parentElement.click();
                $choices.blur();
            }
        };

        if (multipleChoices) {
            // Make container focusable for keyboard interactions.
            $choices.setAttribute('tabindex', '0');

            /**
             * Dropdown list container for available choices.
             * @type {HTMLElement|null}
             */
            const $choicesList = $choices.querySelector('.choices__list--dropdown');

            /**
             * Search input injected by Choices (removed for this UX).
             * @type {HTMLInputElement|null}
             */
            const $choicesSearch = $choices.querySelector('input[type="search"]');

            if (choicesID != null) {
                // Remove search input for simplified multi-select UX.
                if ($choicesSearch) {
                    $choicesSearch.remove(); // we remove the search input because we don't need it here
                }

                /**
                 * Allow de-selecting items by clicking them in the dropdown.
                 * NOTE: This uses an external reference `this.selects[index]` in the original codebase.
                 * If you mount multiple FormSelects, ensure that reference exists in your integration.
                 */
                $choicesList.addEventListener('click', (event) => {
                    const $choiceItem = event.target.closest('[data-id]');
                    if ($choiceItem && $choiceItem.classList.contains('is-selected')) {
                        // This assumes a surrounding controller provides `this.selects[index]`.
                        // If not available in your environment, replace with `this.formSelect.removeActiveItemsByValue(...)`.
                        this.formSelect.removeActiveItemsByValue($choiceItem.dataset.value);
                    }
                });

                // Close on Tab key to avoid trapping focus when options are not tabbable.
                $choices.addEventListener('keydown', (e) => {
                    if (e.code === 'Tab' || e.code === 'Tabs') {
                        autoCloseSelect();
                    }
                });

                // Defensive: also close when container itself is clicked (outside pills/menu).
                $choices.onclick = () => {
                    autoCloseSelect();
                };
            }
        }
    }
}

export { FormSelect };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$formSelect = $context.querySelectorAll('[data-select="root"]');
        for (let i = 0; i < $$formSelect.length; i++) {
            const $formSelect = $$formSelect[i];
            const formSelectAPI = new FormSelect($formSelect);
            $formSelect.formSelectAPI = formSelectAPI;
        }
    }
});
